import { useCallback } from "react";
import { readCreateAgentStream } from "@/lib/agent/createAgentStream";
import { appendVisibleThinkingStep } from "@/lib/agent/visibleThinkingTrace";
import { presentThinkingStatus } from "@/lib/agent/presentThinkingTrace";
import { logger } from "@/utils/logger";
import {
  AgentActionPayload,
  AgentActionPlan,
  AgentComposerRecovery,
  AgentComposerMode,
  AgentMessage,
  AgentThinkingTraceStep,
  AgentVisibleLoopStep,
  AgentResponse,
  AgentPhase,
  AgentReasoning,
  AgentSourceLink,
  AgentWidget,
  DesignBrief,
  DressConcept,
  PresetOption,
} from "@/types/createAgent";
import { CreateFormData } from "@/components/create/types";
import type { DesignSpec } from "@/types/designSpec";
import type { FirstSessionEntryPoint } from "@/types/firstSessionEntryPoint";
import {
  getUserFriendlyError,
  logAgentError,
  isRetryableError,
  isRateLimitError,
} from "@/services/agentErrorHandler";
import { useSafeCreditsContext } from "@/context/providers/CreditsProvider";
import { useCreatePageStore } from "@/stores/createPageStore";
import { projectSavedBackStaleness } from "@/lib/designer/previewFreshness";
import { getBackDesignSpecForLatestFront } from "@/lib/designer/savedDesign";
import { compileCurrentDesignSpec } from "@/services/turn/brief/briefForm";
import { getAgentSideEffects } from "@/lib/agent/sideEffects";
import {
  prepareAgentExtractedData,
  projectAgentFormPatch,
  projectAgentResponseControls,
  projectAgentSessionId,
} from "@/lib/agent/responseProjection";
import {
  CREATE_CHAT_CREDIT_REQUIRED_CODE,
  shouldShowCreateChatCreditGate,
} from "@/lib/createChatCreditGate";
import { trackEvent } from "@/utils/analytics";
import { ensureAnonymousSession } from "@/lib/auth/anonymousSession";
import { requestVirtualTryOn } from "@/lib/create/virtualTryOnEvents";
import { getOrCreateAnonId } from "../utils/anonId";
import {
  getOrCreateAgentSessionId,
  storeAgentSessionId,
} from "../utils/agentSessionId";
import { useCreateChatFreeTurnLimit } from "../utils/useCreateChatFreeTurnLimit";
import { ActionHookContext } from "./types";

function getLatestVisibleUserMessage(): string {
  const messages = useCreatePageStore.getState().agent.conversation.messages;
  const latestUserMessage = [...messages]
    .reverse()
    .find(message => message.role === "user" && !message.hidden);

  return latestUserMessage?.content ?? "";
}

/**
 * Determines if an error should trigger a retry on the client side
 * Includes network errors, timeouts, and rate limits
 */
function isClientRetryableError(
  error: Error | string,
  status?: number
): boolean {
  // Rate limit errors (429 status)
  if (status === 429) {
    return true;
  }
  // Use the server-side retryable check for network/timeout errors
  if (isRetryableError(error)) {
    return true;
  }
  // Also check for rate limit in error message
  if (isRateLimitError(error)) {
    return true;
  }
  return false;
}

/**
 * Retry wrapper for fetch requests with exponential backoff
 * Only retries for transient errors (network, timeout, rate limit)
 */
async function fetchWithRetry(
  url: string,
  options: RequestInit,
  maxRetries: number = 2
): Promise<Response> {
  let lastError: Error | null = null;
  let lastResponse: Response | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(url, options);

      // If response is OK, return it immediately
      if (response.ok) {
        return response;
      }

      // For non-OK responses, check if we should retry
      lastResponse = response;

      // Parse error to check if it's retryable
      const errorData = await response
        .clone()
        .json()
        .catch(() => ({}));
      const errorMessage = errorData.error || `HTTP ${response.status}`;

      // Only retry for retryable errors
      if (
        attempt < maxRetries &&
        isClientRetryableError(errorMessage, response.status)
      ) {
        // Exponential backoff: 1s, 2s
        const delay = 1000 * (attempt + 1);
        logger.info(
          `Retrying agent request (attempt ${attempt + 1}/${maxRetries})`,
          {
            component: "AgentActions",
            metadata: {
              status: response.status,
              delay,
              errorMessage: errorMessage.substring(0, 100),
            },
          }
        );
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }

      // Non-retryable error or max retries reached, return the response
      return response;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Check if this is an abort error - don't retry
      if (lastError.name === "AbortError") {
        throw lastError;
      }

      // Check if we should retry for network errors
      if (attempt < maxRetries && isClientRetryableError(lastError)) {
        const delay = 1000 * (attempt + 1);
        logger.info(
          `Retrying agent request after network error (attempt ${attempt + 1}/${maxRetries})`,
          {
            component: "AgentActions",
            metadata: {
              error: lastError.message.substring(0, 100),
              delay,
            },
          }
        );
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }

      // Non-retryable error or max retries reached, throw the error
      throw lastError;
    }
  }

  // If we get here with a response, return it (even if it's an error response)
  if (lastResponse) {
    return lastResponse;
  }

  // Otherwise throw the last error
  throw lastError || new Error("Unknown error during fetch retry");
}

/**
 * Helper function to handle ticket creation
 */
async function handleTicketCreation(
  store: any,
  submitTicket: (
    designSpecOverride?: DesignSpec,
    options?: { quoteScope?: "front_only" }
  ) => Promise<unknown>,
  designSpecOverride: DesignSpec | undefined,
  quoteScope: "front_only" | undefined,
  phase: string
): Promise<void> {
  try {
    await submitTicket(
      designSpecOverride,
      quoteScope ? { quoteScope } : undefined
    );
  } catch (ticketError) {
    logger.error("Failed to submit ticket", {
      component: "AgentActions",
      metadata: {
        phase,
        error:
          ticketError instanceof Error
            ? ticketError.message
            : String(ticketError),
      },
    });
    store.setAgentError("We couldn't submit your design. Please try again.");
  }
}

export interface SendAgentMessageOptions {
  /** When adding assistant image(s), which side they belong to—used so modal opens on correct tab */
  assistantImageSide?: "front" | "back";
  hideUserMessage?: boolean;
  displayMessage?: string;
  actionPayload?: AgentActionPayload;
  /** UI-only intent hint; never authorizes a generation side effect. */
  composerMode?: AgentComposerMode;
  assistantImageUrl?: string;
  assistantImageUrls?: string[];
  /** One-time server capability for an automatic generated-image review. */
  imageReviewToken?: string;
  /** Override form state for the API request (merged with store state). Use when caller just updated form and needs to ensure it's sent. */
  formStateOverride?: Partial<CreateFormData>;
  /** Entry choice that activated this first-session turn; telemetry only. */
  entryPoint?: FirstSessionEntryPoint;
  /**
   * Preserve the submitted text for an explicit composer retry when the
   * transport outcome is uncertain. The transcript stays intact because the
   * server may already have persisted the turn.
   */
  recoverComposerOnFailure?: boolean;
  /** Exact visible composer text, before any attachment-only routing copy. */
  composerDraft?: string;
  /** Structured context from a prior uncertain turn; client-only. */
  composerRecovery?: AgentComposerRecovery;
  /**
   * Collapse the multi-step thinking trace into a single in-place status line
   * for this turn. Used by the hidden image-review echo turn: its internal
   * loop otherwise sprays several near-identical "I'll inspect the front
   * render…" step lines that read as duplicate placeholders while the review
   * streams. The live status still updates the single typing indicator; only
   * the accumulated trace steps are suppressed (they are not persisted).
   */
  suppressThinkingTrace?: boolean;
  /** One-shot replay of a plain turn the visitor already submitted pre-auth. */
  resumeAfterAuth?: boolean;
}

export function useAgentActions(
  context: ActionHookContext,
  updateFormData: (data: Partial<CreateFormData>) => void,
  _generateImages: (
    targetStep?: number,
    isChatMode?: boolean,
    designSpecOverride?: DesignSpec
  ) => Promise<void>,
  _generateBackOnly: (designSpecOverride?: DesignSpec) => void,
  submitTicket: (
    designSpecOverride?: DesignSpec,
    options?: { quoteScope?: "front_only" }
  ) => Promise<unknown>,
  syncFormDataToTicket: (formData: Partial<CreateFormData>) => Promise<void>,
  _modifyImage: (
    promptOverride?: string,
    designSpecOverride?: DesignSpec
  ) => Promise<void>,
  _setModifyPrompt: (prompt: string) => void
) {
  const {
    store,
    stateRef,
    session,
    isAnonymous,
    isChatMode,
    identityEpochRef,
    refreshSession,
  } = context;
  const { balance } = useSafeCreditsContext();
  // "Logged in" here means "has an account", which an anonymous session does
  // not. Keeping them on the signed-out side matches what the server's chat
  // gate does with them (metered by IP, no balance bypass), so the client
  // pre-check and the server decision cannot disagree and flip the modal open
  // and shut.
  const isLoggedIn = Boolean(session) && !isAnonymous;
  const hasCredits = typeof balance === "number" ? balance > 0 : undefined;
  const freeTurnLimit = useCreateChatFreeTurnLimit();

  /**
   * Process agent response and trigger appropriate actions
   */
  const processAgentResponse = useCallback(
    async (
      response: AgentResponse,
      assistantMessageId?: string | null,
      requestedModificationSide?: "front" | "back"
    ) => {
      const responseIdentityEpoch = identityEpochRef.current;
      const responseIdentityIsCurrent = () =>
        identityEpochRef.current === responseIdentityEpoch;
      if (!responseIdentityIsCurrent()) return;

      let currentState = stateRef.current;

      try {
        // Validate response structure
        if (!response || typeof response !== "object") {
          throw new Error("Invalid agent response: response is not an object");
        }

        if (!response.message || typeof response.message !== "string") {
          throw new Error(
            "Invalid agent response: message is missing or invalid"
          );
        }

        if (!response.phase) {
          throw new Error("Invalid agent response: phase is missing");
        }

        const sideEffects = getAgentSideEffects(response);
        if (sideEffects.some(effect => effect.type === "reset_flow")) {
          const preservedMessages = stateRef.current.agent.conversation.messages
            .slice(-2)
            .map(message => ({
              role: message.role,
              content: message.content,
              ...(message.imageUrl && { imageUrl: message.imageUrl }),
              ...(message.imageUrls && { imageUrls: message.imageUrls }),
              ...(message.selectedIndex !== undefined && {
                selectedIndex: message.selectedIndex,
              }),
              ...(message.hidden !== undefined && {
                hidden: message.hidden,
              }),
              ...(message.presets && { presets: message.presets }),
              ...(message.widgets && { widgets: message.widgets }),
            }));

          if (!responseIdentityIsCurrent()) return;
          store.resetAll();
          for (const message of preservedMessages) {
            store.addMessage(message);
          }
          store.setPhase(response.phase);
          store.setPresets(
            Array.isArray(response.presets) ? response.presets : []
          );
          stateRef.current = useCreatePageStore.getState();
          return;
        }

        if (
          response.designBrief !== undefined ||
          response.activeConcept !== undefined ||
          response.actionPlan !== undefined
        ) {
          if (!responseIdentityIsCurrent()) return;
          store.setAgentBrainState({
            ...(response.designBrief !== undefined && {
              designBrief: response.designBrief ?? null,
            }),
            ...(response.activeConcept !== undefined && {
              activeConcept: response.activeConcept ?? null,
            }),
            ...(response.actionPlan !== undefined && {
              actionPlan: response.actionPlan ?? null,
            }),
          });
          stateRef.current = {
            ...stateRef.current,
            agent: {
              ...stateRef.current.agent,
              ...(response.designBrief !== undefined && {
                designBrief: response.designBrief ?? null,
              }),
              ...(response.activeConcept !== undefined && {
                activeConcept: response.activeConcept ?? null,
              }),
              ...(response.actionPlan !== undefined && {
                actionPlan: response.actionPlan ?? null,
              }),
            },
          };
          currentState = stateRef.current;
        }

        // Update form data if extracted
        if (
          response.extractedData &&
          typeof response.extractedData === "object"
        ) {
          try {
            // Convert imageUrls to File objects if present
            const processedData = prepareAgentExtractedData(
              { extractedData: response.extractedData, sideEffects },
              stateRef.current.form.data
            );

            // Convert uploadedFrontImageUrls to uploadedFrontImages (File objects)
            if (
              processedData.uploadedFrontImageUrls &&
              Array.isArray(processedData.uploadedFrontImageUrls)
            ) {
              try {
                const filePromises = processedData.uploadedFrontImageUrls.map(
                  async (url: string, index: number) => {
                    const response = await fetch(url);
                    const blob = await response.blob();
                    return new File([blob], `front-upload-${index}.png`, {
                      type: blob.type || "image/png",
                    });
                  }
                );
                processedData.uploadedFrontImages =
                  await Promise.all(filePromises);
                if (!responseIdentityIsCurrent()) return;
              } catch (error) {
                logger.warn("Failed to convert front image URLs to Files", {
                  component: "AgentActions",
                  metadata: {
                    error:
                      error instanceof Error ? error.message : String(error),
                  },
                });
              }
            }

            // Convert uploadedBackImageUrls to uploadedBackImages (File objects)
            if (
              processedData.uploadedBackImageUrls &&
              Array.isArray(processedData.uploadedBackImageUrls)
            ) {
              try {
                const filePromises = processedData.uploadedBackImageUrls.map(
                  async (url: string, index: number) => {
                    const response = await fetch(url);
                    const blob = await response.blob();
                    return new File([blob], `back-upload-${index}.png`, {
                      type: blob.type || "image/png",
                    });
                  }
                );
                processedData.uploadedBackImages =
                  await Promise.all(filePromises);
                if (!responseIdentityIsCurrent()) return;
              } catch (error) {
                logger.warn("Failed to convert back image URLs to Files", {
                  component: "AgentActions",
                  metadata: {
                    error:
                      error instanceof Error ? error.message : String(error),
                  },
                });
              }
            }

            const reconciledProcessedData = projectAgentFormPatch(
              stateRef.current.form.data,
              processedData,
              getLatestVisibleUserMessage()
            );
            if (!responseIdentityIsCurrent()) return;
            updateFormData(reconciledProcessedData);
            // Keep the local ref in sync so gating uses the latest extracted data.
            stateRef.current = {
              ...stateRef.current,
              form: {
                ...stateRef.current.form,
                data: {
                  ...stateRef.current.form.data,
                  ...reconciledProcessedData,
                },
              },
            };
            currentState = stateRef.current;

            // A quote side effect immediately commits this same form snapshot.
            // Running the legacy sync first was the first of the two canonical
            // PUTs seen for restored submitted sessions; submission then sent
            // the second. Skip the redundant write on every finalizing turn.
            const finalizesTicket = sideEffects.some(
              effect =>
                effect.type === "create_ticket" ||
                effect.type === "request_location"
            );
            if (!finalizesTicket) {
              // Keep legacy display fields in sync on non-finalizing turns.
              // syncFormDataToTicket intentionally excludes design_spec: the
              // contract is committed only after a render succeeds.
              try {
                await syncFormDataToTicket(reconciledProcessedData);
              } catch (syncError) {
                // Log but don't block - local form reconciliation succeeded.
                logger.warn("Failed to sync form data to ticket", {
                  component: "AgentActions",
                  metadata: {
                    error:
                      syncError instanceof Error
                        ? syncError.message
                        : String(syncError),
                  },
                });
              }
            }
            if (!responseIdentityIsCurrent()) return;
          } catch (formError) {
            logger.warn("Failed to update form data from agent response", {
              component: "AgentActions",
              metadata: {
                error:
                  formError instanceof Error
                    ? formError.message
                    : String(formError),
              },
            });
          }
        }

        // Project controls against the accepted draft with the same rules as
        // the local harness, after browser-specific attachment processing.
        const controls = projectAgentResponseControls({
          response,
          fallbackPhase: currentState.agent.phase,
          formState: currentState.form.data,
          hasFrontImage: Boolean(currentState.generation.images.front.url),
          hasBackImage: Boolean(currentState.generation.images.back.url),
        });
        if (!responseIdentityIsCurrent()) return;
        store.setPhase(controls.phase);
        store.setPresets(controls.presets);
        if (assistantMessageId) {
          store.setMessagePresets(assistantMessageId, controls.presets);
          store.setMessageWidgets(assistantMessageId, controls.widgets);
        }

        const lastGenAt =
          currentState.generation?.progress?.lastGenerationCompletedAt;
        const imageJustGenerated =
          lastGenAt != null && Date.now() - lastGenAt < 5000;

        // Handle side effects after state reconciliation.
        const isTrustedActionPayloadResponse =
          response.trace?.sideEffectSource === "action_payload";
        for (const effect of sideEffects) {
          if (!responseIdentityIsCurrent()) return;
          if (effect.type === "generate_image") {
            if (!isTrustedActionPayloadResponse) {
              logger.warn("Blocked agent generation side effect", {
                component: "AgentActions",
                metadata: {
                  generateType: effect.generateType,
                  phase: response.phase,
                  isChatMode,
                  imageJustGenerated,
                },
              });
              continue;
            }

            if (effect.generateType === "front") {
              await _generateImages(2, true, effect.designSpec);
              if (!responseIdentityIsCurrent()) return;
              continue;
            }

            if (effect.generateType === "back") {
              await _generateBackOnly(effect.designSpec);
              if (!responseIdentityIsCurrent()) return;
              continue;
            }

            if (effect.generateType === "modify") {
              if (effect.modificationPrompt) {
                _setModifyPrompt(effect.modificationPrompt);
              }
              // The customer can inspect another view while this agent turn
              // is pending. Keep the paid edit on the side they requested.
              if (requestedModificationSide) {
                store.setCurrentView(requestedModificationSide);
              }
              if (
                requestedModificationSide === "back" &&
                projectSavedBackStaleness(
                  useCreatePageStore.getState().generation.images
                ).stale
              ) {
                // Back edits after a Front change must start from that Front,
                // not modify pixels belonging to the older Back image. Signed
                // actions may carry only an edit prompt, so put that request
                // into the new render contract before spending a credit.
                const latest = useCreatePageStore.getState();
                const requested =
                  effect.designSpec ??
                  compileCurrentDesignSpec(
                    latest.agent.designBrief,
                    latest.form.data,
                    { hasFrontImage: true, hasBackImage: true }
                  );
                const backSpec = getBackDesignSpecForLatestFront(
                  latest.generation.images,
                  requested,
                  effect.modificationPrompt
                );
                if (!backSpec) {
                  store.setError(
                    "Save the front preview before updating the back."
                  );
                  continue;
                }
                if (
                  effect.modificationPrompt?.trim() &&
                  backSpec.visual.backPrompt
                ) {
                  const backPrompt = backSpec.visual.backPrompt;
                  store.updateFormData({ backPrompt });
                  const brief = useCreatePageStore.getState().agent.designBrief;
                  if (brief) {
                    store.setAgentBrainState({
                      designBrief: {
                        ...brief,
                        back: {
                          ...brief.back,
                          prompt: backPrompt,
                          details: [backPrompt],
                        },
                        provenance: {
                          ...brief.provenance,
                          "visual.backPrompt": {
                            source: "explicit_user",
                            confirmed: true,
                          },
                        },
                      },
                    });
                  }
                }
                await _generateBackOnly(backSpec);
              } else {
                await _modifyImage(
                  effect.modificationPrompt,
                  effect.designSpec
                );
              }
              if (!responseIdentityIsCurrent()) return;
              continue;
            }

            continue;
          }

          if (effect.type === "open_virtual_try_on") {
            if (!isTrustedActionPayloadResponse) {
              logger.warn("Blocked agent virtual try-on side effect", {
                component: "AgentActions",
                metadata: { phase: response.phase, isChatMode },
              });
              continue;
            }
            requestVirtualTryOn();
            continue;
          }

          // Location is no longer collected up front (checkout gathers the
          // real shipping address), so a legacy `request_location` effect is
          // treated the same as `create_ticket`: finalize and submit.
          if (
            effect.type === "create_ticket" ||
            effect.type === "request_location"
          ) {
            await handleTicketCreation(
              store,
              submitTicket,
              effect.type === "create_ticket" ? effect.designSpec : undefined,
              effect.type === "create_ticket" ? effect.quoteScope : undefined,
              response.phase
            );
            if (!responseIdentityIsCurrent()) return;
            continue;
          }
        }

        // 4. Log what agent responded with
        logger.debug(
          `Agent response: ${response.message} [phase: ${response.phase}]`
        );
      } catch (error) {
        if (!responseIdentityIsCurrent()) return;
        logger.error(
          "Failed to process agent response",
          {
            component: "AgentActions",
            metadata: {
              phase: response.phase,
            },
          },
          error instanceof Error ? error : new Error(String(error))
        );
        store.setAgentError(
          error instanceof Error
            ? error.message
            : "Failed to process agent response"
        );
      }
    },
    [
      store,
      stateRef,
      updateFormData,
      submitTicket,
      syncFormDataToTicket,
      isChatMode,
      _generateImages,
      _generateBackOnly,
      _modifyImage,
      _setModifyPrompt,
      identityEpochRef,
    ]
  );

  /**
   * Send message to agent API and process streaming response
   * Works without authentication - chat is available to all users
   */
  const sendAgentMessage = useCallback(
    async (
      message: string,
      imageUrl?: string,
      imageUrls?: string[],
      options?: SendAgentMessageOptions
    ) => {
      let currentState = stateRef.current;
      const displayMessage = options?.displayMessage ?? message;
      const actionPayload = options?.actionPayload;
      const requestedModificationSide =
        actionPayload?.actionId === "modify_dress_back"
          ? "back"
          : actionPayload?.actionId === "modify_dress_front"
            ? "front"
            : useCreatePageStore.getState().ui.view;
      const suppressThinkingTrace = options?.suppressThinkingTrace ?? false;
      const recoverComposerOnFailure =
        options?.recoverComposerOnFailure === true;
      const composerRecovery = options?.composerRecovery;
      const clientTurnId =
        composerRecovery?.clientTurnId ?? crypto.randomUUID();
      const messageToSend = displayMessage;
      const currentTurnIsCustomerAuthored = !(
        options?.hideUserMessage ||
        actionPayload ||
        options?.imageReviewToken
      );
      const resumableSubmittedMessage =
        currentTurnIsCustomerAuthored &&
        !actionPayload &&
        !options?.imageReviewToken &&
        !imageUrl &&
        (!imageUrls || imageUrls.length === 0) &&
        displayMessage.trim()
          ? displayMessage.trim()
          : null;
      const isPlainAuthResume = Boolean(
        options?.resumeAfterAuth && resumableSubmittedMessage
      );
      const authResumePendingMessageId = isPlainAuthResume
        ? useCreatePageStore.getState().agent.pendingSubmittedMessageId
        : null;
      const isOneUseCapabilityTurn = Boolean(
        actionPayload || options?.imageReviewToken
      );
      const identityEpoch = identityEpochRef.current;
      const identityIsCurrent = () =>
        identityEpochRef.current === identityEpoch;
      let requestController: AbortController | null = null;
      let currentUserMessageId: string | null = null;
      let assistantMessageId: string | null = null;
      let receivedCompleteAgentResponse = false;
      let shouldRefreshAnonymousIdentity = false;
      let recoveryCreditGateShown = false;

      const preserveComposerRecovery = () => {
        if (!recoverComposerOnFailure || !displayMessage.trim()) return;
        const currentImageUrls = imageUrls?.length
          ? imageUrls
          : imageUrl
            ? [imageUrl]
            : [];
        store.setPendingComposerSeed(displayMessage);
        const recoveryEntryPoint =
          options?.entryPoint ?? composerRecovery?.entryPoint;
        const recoveryFormStateOverride =
          options?.formStateOverride ?? composerRecovery?.formStateOverride;
        store.setPendingComposerRecovery({
          clientTurnId,
          message: displayMessage,
          composerDraft: options?.composerDraft ?? displayMessage,
          reconciliationMessages: [
            ...(composerRecovery?.reconciliationMessages ??
              (composerRecovery ? [composerRecovery.message] : [])),
            displayMessage,
          ].filter(
            (candidate, index, candidates) =>
              candidates.findIndex(
                value => value.trim() === candidate.trim()
              ) === index
          ),
          imageUrls: currentImageUrls,
          retryImageUrls: currentImageUrls,
          ...(recoveryEntryPoint && { entryPoint: recoveryEntryPoint }),
          ...(recoveryFormStateOverride && {
            formStateOverride: recoveryFormStateOverride,
          }),
          baseSessionVersion:
            composerRecovery?.baseSessionVersion ??
            currentState.agent.sessionVersion,
          baseMessageVersion:
            composerRecovery?.baseMessageVersion ??
            currentState.agent.messageVersion,
          excludedMessageIds: [
            ...(composerRecovery?.excludedMessageIds ?? []),
            ...(currentUserMessageId ? [currentUserMessageId] : []),
            ...(assistantMessageId ? [assistantMessageId] : []),
          ].filter((id, index, ids) => ids.indexOf(id) === index),
        });
      };

      const restoredMessageInterruptedError = (reason: string) =>
        new Error(`Restored message was not completed: ${reason}`);

      const preserveInterruptedAuthResume = () => {
        if (!isPlainAuthResume || !resumableSubmittedMessage) return;
        store.setPendingComposerSeed(resumableSubmittedMessage);
        store.setAgentError(
          "Your message was restored, but we couldn't send it. Select Send below to try again."
        );
        store.setEntryTransition("settled");
      };

      const consumeCompletedAuthResume = () => {
        if (!isPlainAuthResume || !authResumePendingMessageId) return;
        const latestStore = useCreatePageStore.getState();
        if (
          latestStore.agent.pendingSubmittedMessageId ===
          authResumePendingMessageId
        ) {
          latestStore.setPendingSubmittedMessage(null);
        }
      };

      const clearConsumedActionSurface = () => {
        const sourceMessageId = actionPayload?.source?.messageId;
        if (!sourceMessageId) return;
        const sourceMessage = useCreatePageStore
          .getState()
          .agent.conversation.messages.find(
            item => item.id === sourceMessageId
          );
        store.setMessagePresets(sourceMessageId, []);
        if (sourceMessage?.widgets?.length) {
          store.setMessageWidgets(
            sourceMessageId,
            sourceMessage.widgets.map(widget => ({
              ...widget,
              active: false,
              staleReason:
                "Your request was sent. Refresh this page to see the result.",
            }))
          );
        }
      };

      if (
        !isPlainAuthResume &&
        !composerRecovery &&
        shouldShowCreateChatCreditGate({
          turns: currentState.agent.conversation.messages,
          hasCredits,
          isLoggedIn,
          freeTurnLimit,
          currentTurnIsCustomerAuthored,
        })
      ) {
        if (resumableSubmittedMessage && !recoverComposerOnFailure) {
          store.setPendingSubmittedMessage(resumableSubmittedMessage);
        }
        trackEvent("create_chat_credit_gate_shown", {
          journey_stage: "create",
          source: actionPayload ? "preset_action" : "chat_message",
          authenticated: isLoggedIn,
          prior_user_turns: currentState.agent.conversation.messages.filter(
            turn => turn.role === "user"
          ).length,
        });
        store.setShowCreditModal("chat_limit");
        store.setEntryTransition("settled");
        if (recoverComposerOnFailure) {
          preserveComposerRecovery();
          throw new Error(
            "The recovered message is waiting until chat access is available"
          );
        }
        return;
      }

      if (currentTurnIsCustomerAuthored && !isPlainAuthResume) {
        // The turn is about to be sent. Clear the durable replay marker first
        // so reloads and Strict Mode cannot dispatch it a second time.
        store.setPendingSubmittedMessage(null);
      }

      // 1. Log what user sent
      logger.debug(
        `User message: ${messageToSend}${imageUrl || (imageUrls && imageUrls.length > 0) ? " [with image]" : ""}`,
        actionPayload
          ? {
              component: "AgentActions",
              metadata: {
                actionId: actionPayload.actionId,
                action: actionPayload.action,
              },
            }
          : undefined
      );

      // A new customer message or selected preset supersedes failures from
      // earlier workspace actions. Keep those failures if the turn was gated.
      if (currentTurnIsCustomerAuthored || actionPayload) {
        store.clearError();
        store.clearSubmissionError();
      }

      // Clear any previous chat error before starting the turn.
      store.setAgentError(null);
      store.setTyping(true);
      store.setThinkingStatus(null);
      store.setThinkingTrace([]);

      let accumulatedContent = "";
      let liveThinkingSteps: AgentThinkingTraceStep[] = [];

      const completeThinkingTrace = () => {
        if (liveThinkingSteps.length === 0) return;
        liveThinkingSteps = liveThinkingSteps.map(step => ({
          ...step,
          state: "complete" as const,
        }));
        store.setThinkingTrace(liveThinkingSteps);
        if (assistantMessageId) {
          store.setMessageThinkingTrace(assistantMessageId, liveThinkingSteps);
        }
      };

      const appendThinkingStep = (
        status: unknown,
        toolName?: unknown,
        loop?: unknown
      ) => {
        if (typeof status !== "string") return;
        const trimmedStatus = status.trim();
        if (!trimmedStatus) return;
        const normalizedToolName =
          typeof toolName === "string" && toolName.trim()
            ? toolName.trim()
            : undefined;
        const loopRecord =
          loop && typeof loop === "object"
            ? (loop as Record<string, unknown>)
            : null;
        const loopKind =
          loopRecord?.kind === "context" ||
          loopRecord?.kind === "thought" ||
          loopRecord?.kind === "action" ||
          loopRecord?.kind === "observation" ||
          loopRecord?.kind === "decision"
            ? loopRecord.kind
            : "thought";
        const loopPhase =
          loopRecord?.phase === "request" ||
          loopRecord?.phase === "response" ||
          loopRecord?.phase === "error"
            ? loopRecord.phase
            : undefined;
        const loopOutcome =
          loopRecord?.outcome === "ok" || loopRecord?.outcome === "skipped"
            ? loopRecord.outcome
            : undefined;
        const parsedLoop: AgentVisibleLoopStep = {
          kind: loopKind,
          title:
            typeof loopRecord?.title === "string"
              ? loopRecord.title
              : "Designer activity",
          summary:
            typeof loopRecord?.summary === "string"
              ? loopRecord.summary
              : trimmedStatus,
          ...(loopRecord?.source === "llm" ||
          loopRecord?.source === "tool" ||
          loopRecord?.source === "system"
            ? { source: loopRecord.source }
            : {}),
          ...(loopPhase && { phase: loopPhase }),
          ...(loopOutcome && { outcome: loopOutcome }),
          ...(normalizedToolName && { toolName: normalizedToolName }),
          ...(typeof loopRecord?.callId === "string" && loopRecord.callId.trim()
            ? { callId: loopRecord.callId.trim() }
            : {}),
          ...(typeof loopRecord?.iteration === "number"
            ? { iteration: loopRecord.iteration }
            : {}),
        };
        store.setThinkingStatus(presentThinkingStatus(parsedLoop));
        // Hidden echo turns (image review) collapse to a single, in-place
        // status line: keep updating the typing indicator above, but do not
        // accumulate per-event trace steps that render as stacked duplicate
        // placeholders while the review streams.
        if (suppressThinkingTrace) return;
        const nextSteps = appendVisibleThinkingStep(
          liveThinkingSteps,
          trimmedStatus,
          normalizedToolName,
          parsedLoop,
          Date.now()
        );
        if (nextSteps !== liveThinkingSteps) {
          liveThinkingSteps = nextSteps;
          store.setThinkingTrace(liveThinkingSteps);
        }
      };

      try {
        // Cancel any previous in-flight request before starting a new one
        store.abortActiveRequest();
        requestController = new AbortController();
        store.setActiveRequestController(requestController);

        if (session && currentState.agent.sessionId) {
          logger.debug("Using existing agent session", {
            component: "AgentActions",
            metadata: { sessionId: currentState.agent.sessionId },
          });
        }

        if (composerRecovery && currentState.agent.sessionId) {
          // A lost SSE metadata event can happen after the server committed the
          // complete turn. Reconcile that authoritative snapshot before ever
          // minting another turn/credit claim. Only a confirmed 404 (or a truly
          // empty row) is safe to resend.
          const reconciliationResponse = await fetch(
            `/api/create/agent/session?sessionId=${encodeURIComponent(
              currentState.agent.sessionId
            )}`,
            {
              method: "GET",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              signal: requestController.signal,
            }
          );

          if (reconciliationResponse.status !== 404) {
            const reconciliationResult = await reconciliationResponse
              .json()
              .catch(() => null);
            if (
              !reconciliationResponse.ok ||
              !reconciliationResult?.success ||
              !reconciliationResult.data
            ) {
              throw new Error(
                "We couldn't verify whether your previous message was saved. Please try again."
              );
            }

            const restoredData = reconciliationResult.data as {
              sessionId?: string;
              messages?: Array<
                Omit<AgentMessage, "timestamp"> & {
                  timestamp: AgentMessage["timestamp"] | string;
                }
              >;
              phase?: AgentPhase;
              formState?: Partial<CreateFormData>;
              agentState?: {
                designBrief?: DesignBrief | null;
                activeConcept?: DressConcept | null;
                actionPlan?: AgentActionPlan | null;
              };
              sessionVersion?: number;
              messageVersion?: number;
            };
            const restoredMessages = Array.isArray(restoredData.messages)
              ? restoredData.messages.map(restoredMessage => ({
                  ...restoredMessage,
                  timestamp:
                    restoredMessage.timestamp instanceof Date
                      ? restoredMessage.timestamp
                      : new Date(restoredMessage.timestamp),
                }))
              : [];
            const recoveryMessageBoundary = Math.min(
              composerRecovery.baseMessageVersion,
              restoredMessages.length
            );
            const reconciliationMessages = new Set(
              (
                composerRecovery.reconciliationMessages ?? [
                  composerRecovery.message,
                ]
              ).map(candidate => candidate.trim())
            );
            const recoveryImageUrls = composerRecovery.imageUrls;
            let recoveredUserIndex = -1;
            for (let index = restoredMessages.length - 1; index >= 0; index--) {
              const restoredMessage = restoredMessages[index];
              const restoredImageUrls = restoredMessage.imageUrls?.length
                ? restoredMessage.imageUrls
                : restoredMessage.imageUrl
                  ? [restoredMessage.imageUrl]
                  : [];
              const legacyImagesMatch =
                restoredImageUrls.length === recoveryImageUrls.length &&
                restoredImageUrls.every(
                  (url, imageIndex) => url === recoveryImageUrls[imageIndex]
                );
              if (
                restoredMessage.role === "user" &&
                (restoredMessage.clientTurnId ===
                  composerRecovery.clientTurnId ||
                  (index >= recoveryMessageBoundary &&
                    !restoredMessage.clientTurnId &&
                    legacyImagesMatch &&
                    reconciliationMessages.has(restoredMessage.content.trim())))
              ) {
                recoveredUserIndex = index;
                break;
              }
            }
            const hasRecoveredAssistant =
              recoveredUserIndex >= 0 &&
              restoredMessages
                .slice(recoveredUserIndex + 1)
                .some(
                  restoredMessage =>
                    restoredMessage.role === "assistant" &&
                    restoredMessage.content.trim().length > 0
                );
            const restoredSessionVersion = restoredData.sessionVersion ?? 0;
            const restoredMessageVersion =
              restoredData.messageVersion ?? restoredMessages.length;
            const serverAdvancedPastRecoveryBase =
              restoredSessionVersion > composerRecovery.baseSessionVersion ||
              restoredMessageVersion > composerRecovery.baseMessageVersion;

            if (hasRecoveredAssistant && serverAdvancedPastRecoveryBase) {
              store.restoreMessages(restoredMessages);
              if (restoredData.phase) {
                store.setPhase(restoredData.phase);
              }
              if (restoredData.formState) {
                updateFormData(restoredData.formState);
              }
              if (restoredData.agentState) {
                store.setAgentBrainState(restoredData.agentState);
              }
              store.setSessionVersions({
                sessionVersion: restoredSessionVersion,
                messageVersion: restoredMessageVersion,
              });
              store.setAgentError(null);
              store.setPendingComposerSeed(null);
              store.setPendingComposerRecovery(null);
              if (
                restoredMessages[recoveredUserIndex]?.content.trim() !==
                displayMessage.trim()
              ) {
                store.setPendingComposerSeed(displayMessage);
              }
              receivedCompleteAgentResponse = true;
              return;
            }

            if (serverAdvancedPastRecoveryBase) {
              throw new Error(
                "We found newer messages for this dress. Refresh the page before trying again."
              );
            }
          }
        }

        // Add user message to conversation
        store.addMessage({
          role: "user",
          clientTurnId,
          content: displayMessage,
          imageUrl,
          imageUrls,
          hidden: options?.hideUserMessage ?? false,
        });
        const messagesAfterAdd =
          useCreatePageStore.getState().agent.conversation.messages;
        currentUserMessageId =
          messagesAfterAdd[messagesAfterAdd.length - 1]?.id ?? null;

        // Gallery restorations and other already-open chat surfaces bypass the
        // dedicated entry card. Make the first customer-authored edit durable
        // here as well, before the agent request reaches the persistence path.
        // The visible turn is appended first so identity setup never makes the
        // composer feel unresponsive; on failure the turn remains recoverable
        // in the transcript with the ordinary retry error surface.
        if (
          !session &&
          !isPlainAuthResume &&
          !options?.imageReviewToken &&
          (currentTurnIsCustomerAuthored || Boolean(actionPayload))
        ) {
          const identityResult = await ensureAnonymousSession();
          if (!identityIsCurrent()) return;
          if (!identityResult.ok) {
            throw new Error(
              "We couldn't save your design and chat. Check your connection and try again."
            );
          }
          shouldRefreshAnonymousIdentity = true;
        }

        // Prepare conversation history (excluding the message(s) we just added)
        // Include imageUrl if present so the agent can see images
        const messagesToExclude = 1;
        const latestState = useCreatePageStore.getState();
        const conversationHistory: Array<{
          id?: string;
          clientTurnId?: string;
          role: "user" | "assistant";
          content: string;
          hidden?: boolean;
          timestamp: string;
          imageUrl?: string;
          imageUrls?: string[];
          selectedIndex?: number;
          presets?: PresetOption[];
          widgets?: AgentWidget[];
          sources?: AgentSourceLink[];
          reasoning?: AgentReasoning;
        }> = latestState.agent.conversation.messages
          .slice(0, -messagesToExclude)
          .filter(
            historyMessage =>
              !composerRecovery?.excludedMessageIds.includes(historyMessage.id)
          )
          // Hidden turns (preset clicks, uploads) STAY in the history sent to
          // the agent and persisted server-side — they are real turns whose
          // labels carry the design intent; only their bubbles are unrendered.
          .map(msg => ({
            id: msg.id,
            ...(msg.clientTurnId && { clientTurnId: msg.clientTurnId }),
            role: msg.role,
            content: msg.content,
            ...(msg.hidden ? { hidden: true } : {}),
            timestamp: msg.timestamp.toISOString(),
            ...(msg.imageUrl && { imageUrl: msg.imageUrl }),
            ...(msg.imageUrls && { imageUrls: msg.imageUrls }),
            ...(msg.selectedIndex !== undefined && {
              selectedIndex: msg.selectedIndex,
            }),
            ...(msg.presets && { presets: msg.presets }),
            ...(msg.widgets && { widgets: msg.widgets }),
            ...(msg.sources && { sources: msg.sources }),
            // Sent so a re-persist stays byte-stable: the reasoning trace is
            // display-only, and the server used to drop it, which quietly made
            // every replayed message lossier than the one it replaced.
            ...(msg.reasoning && { reasoning: msg.reasoning }),
          }));

        // Build form state for API: merge override (e.g. from entry point) with store
        const baseFormState = {
          ...latestState.form.data,
          ...options?.formStateOverride,
        };
        // When user selected a preset with data (occasion, vibe bundle, single-field), ensure
        // it's in the request even if store update hasn't propagated yet
        const formStateWithPreset =
          actionPayload?.data &&
          typeof actionPayload.data === "object" &&
          Object.keys(actionPayload.data).length > 0
            ? { ...baseFormState, ...actionPayload.data }
            : baseFormState;
        // Derive upload counts from the merged request snapshot. Entry and
        // recovery callers update the store and send in the same tick, so the
        // hook ref can still hold the prior form state even though the explicit
        // override already contains the authoritative references.
        const uploadedFrontImageCount =
          formStateWithPreset.uploadedFrontImages?.length ||
          formStateWithPreset.uploadedFrontImageUrls?.length ||
          formStateWithPreset.uploadedFrontImageCount ||
          0;
        const uploadedBackImageCount =
          formStateWithPreset.uploadedBackImages?.length ||
          formStateWithPreset.uploadedBackImageUrls?.length ||
          formStateWithPreset.uploadedBackImageCount ||
          0;
        const currentFormStateForApi = {
          ...formStateWithPreset,
          // Include image URLs so the agent knows if images have been generated
          ...(latestState.generation.images.front.url && {
            imageUrl: latestState.generation.images.front.url,
          }),
          ...(latestState.generation.images.back.url && {
            backImageUrl: latestState.generation.images.back.url,
          }),
          uploadedFrontImageCount,
          uploadedBackImageCount,
          ...(isLoggedIn ? { isLoggedIn } : { isLoggedIn: false }),
          ...(isLoggedIn && typeof hasCredits === "boolean"
            ? { hasCredits }
            : {}),
        };

        // Real render state so the agent can be honest about a render that is
        // still in flight or that just failed (rather than narrating success).
        const genProgress = latestState.generation.progress;
        let lastGeneration:
          | { status: "pending" | "failed"; side: "front" | "back" }
          | undefined;
        if (genProgress.loading) {
          // While rendering, phase still names the side being generated.
          lastGeneration = {
            status: "pending",
            side: genProgress.phase === "back" ? "back" : "front",
          };
        } else if (
          genProgress.hasFailedGeneration &&
          !genProgress.failedSignalConsumed
        ) {
          // A failure resets phase to "idle", so read the failed side from the
          // step it left us on (2 = front, 4 = back). Report the failure once so
          // the agent doesn't keep re-apologizing on later unrelated turns.
          lastGeneration = {
            status: "failed",
            side: latestState.form.currentStep === 4 ? "back" : "front",
          };
          useCreatePageStore.getState().markFailedSignalConsumed();
        }

        /**
         * Adopts the server's session id.
         *
         * The old rule was "adopt only if we have none", which made the
         * server's replacement id unadoptable — the client kept talking to an
         * id the server had already abandoned. `sessionReplaced` is the
         * server's explicit instruction that the id changed (its previous
         * owner is a different account), and it is obeyed unconditionally.
         * Returns whether the local id changed.
         */
        const adoptServerSessionId = (
          agentResponse: AgentResponse
        ): boolean => {
          const localSessionId = stateRef.current.agent.sessionId;
          const serverSessionId = projectAgentSessionId(
            localSessionId,
            agentResponse
          );
          if (!serverSessionId || serverSessionId === localSessionId) {
            return false;
          }

          store.setSessionId(serverSessionId);
          storeAgentSessionId(serverSessionId);
          stateRef.current = {
            ...stateRef.current,
            agent: {
              ...stateRef.current.agent,
              sessionId: serverSessionId,
            },
          };
          return true;
        };

        // Mint the conversation id BEFORE the request and commit it to the
        // store + localStorage synchronously. Waiting for the server to mint
        // one meant two quick turns both left with a null id and each came back
        // with a different session — the duplicate-row source. No turn is ever
        // sent id-less once a conversation has started.
        const requestSessionId = getOrCreateAgentSessionId(
          latestState.agent.sessionId
        );
        if (
          requestSessionId &&
          requestSessionId !== currentState.agent.sessionId
        ) {
          store.setSessionId(requestSessionId);
          stateRef.current = {
            ...stateRef.current,
            agent: {
              ...stateRef.current.agent,
              sessionId: requestSessionId,
            },
          };
          currentState = stateRef.current;
        }

        // Capability-bearing turns are strict one-use operations. Retrying the
        // same consumed action/review request could execute the model against
        // a now-invalid surface, so only ordinary customer chat auto-retries.
        const requestBody = {
          clientTurnId,
          // Durable anon id sent for both signed-in and signed-out turns, so
          // pre-login and post-login activity from the same browser can be
          // joined in agent_events.
          anonId: getOrCreateAnonId(),
          message: messageToSend,
          conversationHistory,
          actionPayload,
          currentFormState: currentFormStateForApi,
          currentPhase: latestState.agent.phase,
          ...(options?.entryPoint && {
            entryPoint: options.entryPoint,
          }),
          ...(options?.composerMode && { composerMode: options.composerMode }),
          ...(lastGeneration && { lastGeneration }),
          designBrief: latestState.agent.designBrief,
          activeConcept: latestState.agent.activeConcept,
          actionPlan: latestState.agent.actionPlan,
          sessionId: requestSessionId,
          sessionVersion: latestState.agent.sessionVersion,
          messageVersion: latestState.agent.messageVersion,
          ...(imageUrl && { imageUrl }),
          ...(imageUrl &&
            options?.assistantImageSide && {
              imageSide: options.assistantImageSide,
            }),
          ...(imageUrls && { imageUrls }),
          ...(options?.imageReviewToken && {
            imageReviewToken: options.imageReviewToken,
          }),
          ...(options?.hideUserMessage && { hideCurrentUserMessage: true }),
        };
        const response = await fetchWithRetry(
          "/api/create/agent",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            signal: requestController.signal,
            body: JSON.stringify(requestBody),
          },
          isOneUseCapabilityTurn ? 0 : 2
        );

        if (!identityIsCurrent()) {
          if (isPlainAuthResume) {
            throw restoredMessageInterruptedError("the account changed");
          }
          return;
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          if (
            response.status === 402 &&
            errorData.details?.code === CREATE_CHAT_CREDIT_REQUIRED_CODE
          ) {
            if (currentUserMessageId) {
              store.removeMessage(currentUserMessageId);
            }
            // A 402 proves the server did not execute this turn. Re-record it
            // even for an auth resume (with a fresh one-shot id) so a stale
            // client balance cannot silently discard the customer's words.
            if (resumableSubmittedMessage && !recoverComposerOnFailure) {
              store.setPendingSubmittedMessage(null);
              store.setPendingSubmittedMessage(resumableSubmittedMessage);
            }
            store.setShowCreditModal("chat_limit");
            store.setEntryTransition("settled");
            trackEvent("create_chat_credit_gate_shown", {
              journey_stage: "create",
              source: "server_enforcement",
              authenticated: Boolean(errorData.details?.authenticated),
              prior_user_turns: Number(errorData.details?.turnsUsed ?? 0),
            });
            if (recoverComposerOnFailure) {
              recoveryCreditGateShown = true;
              preserveComposerRecovery();
              throw new Error(
                "The recovered message is waiting until chat access is available"
              );
            }
            return;
          }
          if (
            response.status === 409 &&
            errorData.details?.code === "CREATE_CHAT_TURN_ALREADY_USED"
          ) {
            clearConsumedActionSurface();
            throw new Error(
              "Your request was already sent. Refresh this page to see the latest result."
            );
          }
          // Include status code in error message for better error detection
          // Rate limit (429), server errors (500+), and client errors (400+)
          const statusText =
            response.status === 429
              ? "rate limit exceeded"
              : `status ${response.status}`;
          throw new Error(
            errorData.error || `Failed to get agent response (${statusText})`
          );
        }

        // Check if response is streaming (text/event-stream)
        const contentType = response.headers.get("content-type");
        const isStreaming = contentType?.includes("text/event-stream");

        if (!isStreaming) {
          // Fallback to non-streaming response
          const result = await response.json();
          if (!identityIsCurrent()) {
            if (isPlainAuthResume) {
              throw restoredMessageInterruptedError("the account changed");
            }
            return;
          }
          if (!result.success || !result.data) {
            throw new Error(result.error || "Invalid response from agent");
          }

          const agentResponse: AgentResponse = result.data;
          if (!agentResponse || !agentResponse.message) {
            throw new Error("Invalid agent response: missing message");
          }

          if (adoptServerSessionId(agentResponse)) {
            currentState = stateRef.current;
          }

          if (
            typeof agentResponse.sessionVersion === "number" &&
            typeof agentResponse.messageVersion === "number"
          ) {
            store.setSessionVersions({
              sessionVersion: agentResponse.sessionVersion,
              messageVersion: agentResponse.messageVersion,
            });
          }
          if (typeof agentResponse.syncConflict === "string") {
            store.setAgentError(
              `restore_conflict: ${agentResponse.syncConflict}`
            );
          }

          // 4. Log what agent responded with
          logger.debug(
            `Agent response: ${agentResponse.message} [phase: ${agentResponse.phase}]`
          );

          // Add assistant message
          store.addMessage({
            role: "assistant",
            content: agentResponse.message,
            ...(agentResponse.sources &&
              agentResponse.sources.length > 0 && {
                sources: agentResponse.sources,
              }),
            ...(options?.assistantImageUrl && {
              imageUrl: options.assistantImageUrl,
            }),
            ...(options?.assistantImageUrls &&
              options.assistantImageUrls.length > 0 && {
                imageUrls: options.assistantImageUrls,
              }),
            ...(options?.assistantImageSide && {
              imageSide: options.assistantImageSide,
            }),
          });
          const nonStreamingMessages =
            useCreatePageStore.getState().agent.conversation.messages;
          const nonStreamingAssistantMessageId =
            nonStreamingMessages[nonStreamingMessages.length - 1]?.role ===
            "assistant"
              ? nonStreamingMessages[nonStreamingMessages.length - 1]?.id
              : null;
          assistantMessageId = nonStreamingAssistantMessageId;

          await processAgentResponse(
            agentResponse,
            nonStreamingAssistantMessageId,
            requestedModificationSide
          );
          receivedCompleteAgentResponse = true;
          consumeCompletedAuthResume();
          return;
        }

        // Handle streaming response
        const responseStream = response.body;
        if (!responseStream) {
          throw new Error("Response body is not readable");
        }

        // Create initial assistant message with empty content
        store.addMessage({
          role: "assistant",
          content: "",
          ...(options?.assistantImageUrl && {
            imageUrl: options.assistantImageUrl,
          }),
          ...(options?.assistantImageUrls &&
            options.assistantImageUrls.length > 0 && {
              imageUrls: options.assistantImageUrls,
            }),
          ...(options?.assistantImageSide && {
            imageSide: options.assistantImageSide,
          }),
        });

        // Get the message ID - get it directly from the store state (Zustand updates are synchronous)
        // Use getState() to ensure we have the latest state immediately after addMessage
        const storeState = useCreatePageStore.getState();
        const messages = storeState.agent.conversation.messages;
        const lastMessage = messages[messages.length - 1];

        if (lastMessage && lastMessage.role === "assistant") {
          assistantMessageId = lastMessage.id;
        } else {
          // Fallback: find the last assistant message
          const lastAssistantMessage = [...messages]
            .reverse()
            .find(msg => msg.role === "assistant");

          if (lastAssistantMessage) {
            assistantMessageId = lastAssistantMessage.id;
            logger.debug("Found assistant message using fallback method", {
              component: "AgentActions",
              metadata: { messageId: assistantMessageId },
            });
          } else {
            logger.warn("Could not find assistant message after adding", {
              component: "AgentActions",
              metadata: {
                messageCount: messages.length,
                lastMessageRole: lastMessage?.role,
                allRoles: messages.map(m => m.role),
              },
            });
          }
        }

        // Browser and lab consume the same framing, EOF and cancellation contract.
        for await (const data of readCreateAgentStream(
          responseStream,
          requestController.signal
        )) {
          if (!identityIsCurrent()) {
            if (isPlainAuthResume)
              throw restoredMessageInterruptedError("the account changed");
            return;
          }
          // A repeated terminal frame must never execute effects twice.
          if (receivedCompleteAgentResponse) continue;
          if (data.type === "text") {
            // Update message content incrementally
            accumulatedContent += data.content;

            logger.debug("Received text chunk", {
              component: "AgentActions",
              metadata: {
                chunkLength: data.content?.length || 0,
                accumulatedLength: accumulatedContent.length,
                hasMessageId: !!assistantMessageId,
              },
            });

            if (assistantMessageId) {
              store.updateMessageContent(
                assistantMessageId,
                accumulatedContent
              );
            } else {
              // Fallback: find and update the last assistant message
              const currentStoreState = useCreatePageStore.getState();
              const currentMessages =
                currentStoreState.agent.conversation.messages;
              const lastAssistantMessage = [...currentMessages]
                .reverse()
                .find(msg => msg.role === "assistant");

              if (lastAssistantMessage) {
                store.updateMessageContent(
                  lastAssistantMessage.id,
                  accumulatedContent
                );
              }
            }
          } else if (data.type === "stream_complete") {
            // Text streaming complete - structured data updates will follow.
            // Show loading state while presets are being extracted.
            logger.debug("Stream complete, awaiting structured metadata", {
              component: "AgentActions",
              metadata: {
                messageLength: data.message?.length || 0,
              },
            });
            // Hide typing indicator once the user-facing text is complete.
            store.setTyping(false);
            store.setEntryTransition("settled");
            completeThinkingTrace();
            store.setThinkingStatus("Preparing next options");
            store.setPresetsLoading(true);
          } else if (data.type === "status") {
            appendThinkingStep(data.status, data.toolName, data.loop);
          } else if (data.type === "metadata") {
            // Final metadata chunk - process the response
            const agentResponse: AgentResponse = data.data;
            store.setEntryTransition("settled");

            // 4. Log what agent responded with
            logger.debug(
              `Agent response: ${agentResponse.message} [phase: ${agentResponse.phase}]`
            );

            // Persist sessionId if the API returned it
            if (adoptServerSessionId(agentResponse)) {
              currentState = stateRef.current;
            }
            if (
              typeof agentResponse.sessionVersion === "number" &&
              typeof agentResponse.messageVersion === "number"
            ) {
              store.setSessionVersions({
                sessionVersion: agentResponse.sessionVersion,
                messageVersion: agentResponse.messageVersion,
              });
            }
            if (typeof agentResponse.syncConflict === "string") {
              store.setAgentError(
                `restore_conflict: ${agentResponse.syncConflict}`
              );
              if (isPlainAuthResume && resumableSubmittedMessage) {
                store.setPendingComposerSeed(resumableSubmittedMessage);
              }
            }

            // Clear presets loading state
            store.setPresetsLoading(false);
            completeThinkingTrace();
            store.setThinkingStatus(null);

            // Validate response has required fields
            if (
              !agentResponse ||
              !agentResponse.message ||
              !agentResponse.phase
            ) {
              throw new Error(
                "Invalid agent response: missing required fields"
              );
            }

            // Ensure message content is set to final value
            // Always update to ensure the message is set, even if no text chunks came through
            if (assistantMessageId) {
              // Update existing message if we have the ID
              store.updateMessageContent(
                assistantMessageId,
                agentResponse.message
              );
              store.setMessageSources(
                assistantMessageId,
                agentResponse.sources
              );
            } else {
              // Fallback: if we don't have the message ID, find the last assistant message and update it
              // or add a new message if none exists
              const currentStoreState = useCreatePageStore.getState();
              const currentMessages =
                currentStoreState.agent.conversation.messages;
              const lastAssistantMessage = [...currentMessages]
                .reverse()
                .find(msg => msg.role === "assistant");

              if (lastAssistantMessage) {
                // Update the last assistant message
                store.updateMessageContent(
                  lastAssistantMessage.id,
                  agentResponse.message
                );
                store.setMessageSources(
                  lastAssistantMessage.id,
                  agentResponse.sources
                );
              } else {
                // If no assistant message exists, add one
                store.addMessage({
                  role: "assistant",
                  content: agentResponse.message,
                  ...(agentResponse.sources &&
                    agentResponse.sources.length > 0 && {
                      sources: agentResponse.sources,
                    }),
                });
              }
            }

            logger.info("Agent response received", {
              component: "AgentActions",
              metadata: {
                message: agentResponse.message.substring(0, 50),
                phase: agentResponse.phase,
                presetCount: agentResponse.presets?.length || 0,
                hasActions: !!agentResponse.actions,
                hasMessageId: !!assistantMessageId,
              },
            });

            // Process the response (update form, trigger actions, etc.)
            await processAgentResponse(
              agentResponse,
              assistantMessageId,
              requestedModificationSide
            );
            receivedCompleteAgentResponse = true;
            // Structured metadata is the terminal success event. Stop reading
            // now: duplicate frames cannot replay side effects, and a server
            // leaving SSE open cannot keep this completed request busy. The
            // shared reader cancels the transport and releases its lock.
            break;
          } else if (data.type === "error") {
            // Error chunk - clear presets loading state
            store.setPresetsLoading(false);
            completeThinkingTrace();
            store.setThinkingStatus(null);
            // For timeout errors, show a friendlier message that doesn't replace the partial response
            const isTimeoutError = data.errorType === "timeout";
            if (isTimeoutError) {
              if (isOneUseCapabilityTurn) {
                clearConsumedActionSurface();
                if (currentUserMessageId) {
                  store.removeMessage(currentUserMessageId);
                  currentUserMessageId = null;
                }
              }
              store.setAgentError(
                isOneUseCapabilityTurn
                  ? "Your request may have finished, but we didn’t receive the result. Refresh this page to check."
                  : data.error
              );
              // Update the message to indicate it was incomplete
              if (assistantMessageId && accumulatedContent.length > 0) {
                // Keep partial content with a note
                store.updateMessageContent(
                  assistantMessageId,
                  accumulatedContent +
                    "\n\n_(Response timed out - please try again)_"
                );
              } else if (assistantMessageId) {
                store.updateMessageContent(
                  assistantMessageId,
                  "_(Response timed out - please send your message again)_"
                );
              }
              if (isPlainAuthResume) {
                throw restoredMessageInterruptedError(
                  data.error || "the response timed out"
                );
              }
              if (recoverComposerOnFailure) {
                throw new Error(
                  data.error || "The designer response timed out"
                );
              }
              return; // Exit gracefully instead of throwing
            }
            throw new Error(data.error || "Unknown error from agent");
          }
        }
        if (!receivedCompleteAgentResponse) {
          if (isPlainAuthResume) {
            throw restoredMessageInterruptedError(
              "the response ended before its final update"
            );
          }
          if (recoverComposerOnFailure) {
            throw new Error(
              "The designer response ended before its final update"
            );
          }
        }
        consumeCompletedAuthResume();
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          logger.debug("Agent request aborted", {
            component: "AgentActions",
          });
          if (isPlainAuthResume) {
            preserveInterruptedAuthResume();
            throw error;
          }
          // A replaced request or an unmounted /create tree has a different
          // (or no) active controller. Its successor/cleanup owns the shared
          // activity state, so the retired request must stay quiet. If this
          // controller is STILL current, however, the transport aborted on its
          // own. Treat that as an uncertain failure below instead of silently
          // leaving a selected capability with no explanation.
          const abortBelongsToRetiredRequest =
            !identityIsCurrent() ||
            useCreatePageStore.getState().agent.activeRequestController !==
              requestController;
          if (abortBelongsToRetiredRequest) {
            return;
          }
        }

        const isCurrentRequest =
          identityIsCurrent() &&
          useCreatePageStore.getState().agent.activeRequestController ===
            requestController;
        if (!isCurrentRequest) {
          if (isPlainAuthResume) {
            preserveInterruptedAuthResume();
            throw error instanceof Error
              ? error
              : restoredMessageInterruptedError("the request was replaced");
          }
          return;
        }

        if (recoveryCreditGateShown) {
          // The server proved this retry did not execute. Keep its structured
          // text/photo context without replacing the purpose-built credit modal
          // with a generic transport banner.
          preserveComposerRecovery();
          store.setAgentError(null);
          store.setEntryTransition("settled");
          throw error instanceof Error
            ? error
            : new Error(
                "The recovered message is waiting until chat access is available"
              );
        }

        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to send message to agent";

        if (isOneUseCapabilityTurn) {
          clearConsumedActionSurface();
          if (currentUserMessageId) {
            store.removeMessage(currentUserMessageId);
            currentUserMessageId = null;
          }
        }

        logAgentError(
          "AgentActions",
          error instanceof Error ? error : new Error(errorMessage),
          {
            userMessage: message.substring(0, 100),
            errorStack: error instanceof Error ? error.stack : undefined,
          }
        );

        // Provide user-friendly error messages based on error type
        const userFriendlyMessage = isOneUseCapabilityTurn
          ? "Your request may already have been sent. Refresh this page to check the result before trying again."
          : getUserFriendlyError(errorMessage);

        // The transient error banner (ChatErrorBanner) is the ONLY error
        // surface. Canned copy must never be written into the transcript as an
        // assistant turn — it persists into conversation_history, reads as the
        // designer speaking ("I'm sorry, network error…"), and pollutes every
        // later planner turn (zero-canned-copy rule; July session audit).
        store.setAgentError(userFriendlyMessage);
        if (recoverComposerOnFailure && displayMessage.trim()) {
          // Do not roll back the visible turn: a lost metadata event or
          // response can happen after server persistence. Seed an explicit
          // retry instead, and let CreateAgentChat reuse the same image URLs.
          preserveComposerRecovery();
        }
        if (isPlainAuthResume && resumableSubmittedMessage) {
          // The shared one-shot claim cannot be retried automatically after an
          // uncertain network failure. Keep the customer's exact words in the
          // visible composer so recovery is explicit and under their control.
          store.setPendingComposerSeed(resumableSubmittedMessage);
        }
        store.setEntryTransition("settled");

        // A streaming assistant bubble that died mid-turn: keep any real
        // model-authored partial text, but remove an empty shell so the
        // transcript doesn't carry a blank assistant turn.
        if (assistantMessageId) {
          const streamed = useCreatePageStore
            .getState()
            .agent.conversation.messages.find(
              messageItem => messageItem.id === assistantMessageId
            );
          if (!streamed?.content?.trim()) {
            store.removeMessage(assistantMessageId);
          }
        }
        if (isPlainAuthResume || recoverComposerOnFailure) {
          throw error instanceof Error ? error : new Error(errorMessage);
        }
      } finally {
        const isCurrentRequest =
          identityIsCurrent() &&
          useCreatePageStore.getState().agent.activeRequestController ===
            requestController;
        if (isCurrentRequest) {
          store.setActiveRequestController(null);
          store.setTyping(false);
          store.setThinkingStatus(null);
          store.setEntryTransition("settled");
          // Ensure presets loading is cleared even on unexpected errors
          store.setPresetsLoading(false);
        }
        // The cookie already owned the completed server request. Hydrate the
        // React auth provider only after the turn settles so the identity
        // boundary cannot retire a live stream midway through persistence.
        if (shouldRefreshAnonymousIdentity) {
          void Promise.resolve(refreshSession()).catch(() => {});
        }
      }
    },
    [
      store,
      stateRef,
      session,
      isLoggedIn,
      hasCredits,
      freeTurnLimit,
      processAgentResponse,
      identityEpochRef,
      refreshSession,
      updateFormData,
    ]
  );

  return {
    sendAgentMessage,
    processAgentResponse,
  };
}
